import { useEffect, useMemo, useState } from 'react';

import {
  CheckStatus,
  EntityRefUtil,
  formatDateWithUserTimezone,
  formatManuallyWithUserTimezone,
  GetWorkOrdersResponseDTO,
  IssueSeverity,
  IssueSource,
  IssueStatus,
  RecordState,
  WorkOrderPriority,
  WorkOrderStatus,
} from '@gearbelt/global';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { startCase } from 'lodash';

import CheckDrawer from '@/components/checks/check-drawer';
import CheckProgressBar from '@/components/checks/check-progress-bar';
import IssueDrawer from '@/components/issues/issue-drawer';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { TaskProgress } from '@/components/work-orders/task-progress';
import { cn, initials } from '@/lib/utils';
import { checkKeys, getAWAChecksByAssetQueryOptions } from '@/queries/checks';
import { awaWorkOrderKeys } from '@/queries/work-orders';
import { useUserStore } from '@/stores/user.store';
import { formatScheduleCheckLabel } from '@/utils/schedules';
import { getWorkOrderPriorityColorClass } from '@/utils/work-order';

import CloseIcon from '../../icons/close';
import WarningIcon from '../../icons/warning';
import { Avatar, AvatarFallback, AvatarImage } from '../../ui/avatar';

function isWorkOrderPriority(value: string): value is WorkOrderPriority {
  return Object.values(WorkOrderPriority).includes(value as WorkOrderPriority);
}

export default function WorkOrderDrawer({
  workOrder,
  open,
  onOpenChange,
}: {
  workOrder?: GetWorkOrdersResponseDTO['data'][number];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const userTimezone = useUserStore.getState().user?.timezone;
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(open);
  const [selectedCheckId, setSelectedCheckId] = useState<string | null>(null);
  const [selectedIssueId, setSelectedIssueId] = useState<string | null>(null);

  useEffect(() => {
    setIsOpen(open);
  }, [open]);

  const assetId = workOrder?.asset?.id;

  const { data: checksData } = useQuery({
    ...getAWAChecksByAssetQueryOptions({ assetId }),
    enabled: !!assetId,
  });

  const handleOpenChange = (open: boolean) => {
    onOpenChange(open);
    setIsOpen(open);
    if (!open) {
      setSelectedCheckId(null);
      setSelectedIssueId(null);
    }
  };

  const filteredChecks = useMemo(
    () =>
      checksData?.checks?.filter((check) => {
        if (check.workOrder?.id !== workOrder?.id) return false;
        if (check.schedule?.state !== RecordState.Active) {
          return check.status === CheckStatus.Closed;
        }
        return true;
      }) ?? [],
    [checksData, workOrder?.id],
  );

  if (!workOrder) return null;

  const issuesList = workOrder.issues ?? [];
  const closedIssues = issuesList.filter((i) => i.status === IssueStatus.Closed).length;
  const closedChecks = filteredChecks.filter((c) => c.status === CheckStatus.Closed).length;
  const totalTasks = issuesList.length + filteredChecks.length;
  const completedTasks = closedIssues + closedChecks;

  const displayName = EntityRefUtil.createUserFullNameRef(workOrder.user, '—');
  const creatorAvatarUrl = workOrder.sitemateUsers?.find((su) => su.id === workOrder.user?.sitemateUserId)?.avatarUrl || workOrder.user?.avatarUrl;

  return (
    <>
      <Sheet open={isOpen} onOpenChange={handleOpenChange}>
        <SheetContent className="mx-auto h-full w-full flex-col focus:outline-none sm:max-w-[800px]" hideCloseIcon>
          <SheetHeader className="border-grey-3 h-[64px] flex-row items-center justify-between space-y-0 border-b bg-white">
            <SheetTitle>Work order record</SheetTitle>
            <button type="button" className="rounded-sm opacity-70 transition-opacity outline-none hover:opacity-100" onClick={() => handleOpenChange(false)}>
              <CloseIcon className="h-4 w-4" />
              <span className="sr-only">Close</span>
            </button>
          </SheetHeader>

          {/* Content header — ID + Creator + blue underline */}
          <div className="flex flex-col gap-1 px-4 pt-4">
            <div className="flex items-end justify-between gap-2 text-sm">
              <div>
                <span className="font-medium">ID: </span> {EntityRefUtil.createWorkOrderRef(workOrder.number)}
              </div>
              <div className="flex items-center gap-2 text-right">
                <div className="flex flex-col gap-0.5">
                  <div className="line-clamp-1 font-medium">{displayName}</div>
                  <div className="text-secondary-text">
                    {formatManuallyWithUserTimezone(new Date(workOrder.createdAt), 'MMM DD, YYYY hh:mm a', userTimezone)}
                  </div>
                </div>
                <Avatar>
                  {creatorAvatarUrl ? <AvatarImage src={creatorAvatarUrl} alt={displayName} /> : <AvatarFallback>{initials(displayName)}</AvatarFallback>}
                </Avatar>
              </div>
            </div>
            <div className="border-default-blue border-b-4" />
          </div>

          <div className="flex min-h-0 flex-1 flex-col">
            <ScrollArea className="flex-1 px-6">
              <div className="flex flex-col gap-2 pt-4 pb-6">
                {/* Asset */}
                <div className="flex flex-col gap-1">
                  <Label>Asset</Label>
                  <div className="bg-grey-1 ring-grey-3 flex min-h-[41px] flex-wrap items-center gap-1 rounded p-2 ring-2 ring-inset">
                    <span className="text-sm">{workOrder.asset ? EntityRefUtil.createAssetLabel(workOrder.asset) : '—'}</span>
                  </div>
                </div>

                {/* Status */}
                <div className="flex flex-col gap-1">
                  <Label>Status</Label>
                  <div className="bg-grey-1 ring-grey-3 flex min-h-[41px] flex-wrap items-center gap-1 rounded p-2 ring-2 ring-inset">
                    {workOrder.status === WorkOrderStatus.Closed ? (
                      <Badge variant="success" size="sm">
                        Closed
                      </Badge>
                    ) : (
                      <Badge variant="grey" size="sm">
                        Open
                      </Badge>
                    )}
                  </div>
                </div>

                {/* Title */}
                <div className="flex flex-col gap-1">
                  <Label>Title</Label>
                  <div className="bg-grey-1 ring-grey-3 flex min-h-[41px] flex-wrap items-center gap-1 rounded p-2 ring-2 ring-inset">
                    <span className="text-sm">{workOrder.title || '—'}</span>
                  </div>
                </div>

                {/* Progress */}
                <div className="mb-1 flex flex-col gap-1">
                  <Label>Progress</Label>
                  <TaskProgress
                    completed={completedTasks}
                    total={totalTasks}
                    openIssueCount={issuesList.length - closedIssues}
                    openCheckCount={filteredChecks.length - closedChecks}
                  />
                </div>

                {/* Issues */}
                {issuesList.length > 0 && (
                  <div>
                    <Label className="mb-2">Issues</Label>
                    <div className="space-y-2">
                      {issuesList.map((issue) => (
                        <button
                          key={issue.id}
                          type="button"
                          className="flex h-full w-full items-stretch gap-2 rounded-md border p-3 text-left"
                          onClick={() => {
                            setIsOpen(false);
                            setSelectedIssueId(issue.id);
                          }}>
                          <div
                            className={cn('w-1 self-stretch rounded-full', {
                              'bg-default-red': issue.severity === IssueSeverity.Critical || issue.severity === IssueSeverity.High,
                              'bg-default-yellow': issue.severity === IssueSeverity.Medium,
                              'bg-default-green':
                                issue.severity !== IssueSeverity.Critical && issue.severity !== IssueSeverity.High && issue.severity !== IssueSeverity.Medium,
                            })}
                          />
                          <div className="flex-1">
                            <div className="flex items-center justify-between">
                              <span
                                className={cn('max-w-[250px] truncate text-sm font-medium sm:max-w-[300px] md:max-w-[400px] lg:max-w-[500px]', {
                                  'line-through': issue.status === IssueStatus.Closed,
                                })}>
                                {issue.title}
                              </span>
                              <span className="text-secondary-text text-xs">{EntityRefUtil.createIssueRef(issue.number)}</span>
                            </div>
                            <div className="flex items-center justify-between">
                              <p className="text-secondary-text text-sm">{issue.description}</p>
                              <Badge variant={issue.status === IssueStatus.Closed ? 'success' : 'grey'} size="sm">
                                {issue.status === IssueStatus.Closed ? 'Closed' : 'Open'}
                              </Badge>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Schedule Checks */}
                {filteredChecks.length > 0 && (
                  <div>
                    <Label className="mb-2">Schedule Checks</Label>
                    <div className="space-y-2">
                      {filteredChecks.map((check) => {
                        const isClosed = check.status === CheckStatus.Closed;
                        return (
                          <button
                            key={check.id}
                            type="button"
                            className={cn('flex w-full gap-2 rounded-md border p-3 text-left', isClosed && 'bg-grey-1')}
                            onClick={() => {
                              if (check.schedule?.state !== RecordState.Deleted) {
                                setIsOpen(false);
                                setSelectedCheckId(check.id);
                              }
                            }}>
                            <div className="bg-default-blue w-1 shrink-0 self-stretch rounded-full" />
                            <div className="flex flex-1 flex-col gap-2">
                              <div className="flex items-start gap-2">
                                <div className="flex-1">
                                  <span className="text-sm font-medium">{check.schedule?.name || 'Check'}</span>
                                  <div className="text-secondary-text text-sm">Schedule Check: {formatScheduleCheckLabel(check) ?? '-'}</div>
                                </div>
                                <div className="flex shrink-0 flex-col items-end gap-1">
                                  <span className="text-secondary-text text-xs">{check.number ? EntityRefUtil.createCheckRef(check.number) : '-'}</span>
                                  <Badge variant={isClosed ? 'success' : 'grey'} size="sm">
                                    {isClosed ? 'Closed' : 'Open'}
                                  </Badge>
                                </div>
                              </div>
                              <CheckProgressBar
                                isClosed={isClosed}
                                isUnlinked={check.isUnlinked}
                                dueProgress={check.dueProgress}
                                dateRemaining={check.dateRemaining}
                              />
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Users — contributors and legacy Sitemate users assigned to this work order */}
                <div className="flex flex-col gap-1">
                  <Label>Users</Label>
                  <div className="bg-grey-1 ring-grey-3 flex min-h-[41px] flex-wrap items-center gap-1 rounded p-2 ring-2 ring-inset">
                    {(() => {
                      const assignedUsers = [...(workOrder.gearbeltContributors ?? []), ...(workOrder.sitemateUsers ?? [])];
                      return assignedUsers.length > 0 ? (
                        assignedUsers.map((user) => {
                          const name = EntityRefUtil.createUserFullNameRef(user);
                          return (
                            <div key={user.id} className="flex items-center gap-1.5">
                              <Avatar className="h-6 w-6">
                                {user.avatarUrl ? (
                                  <AvatarImage src={user.avatarUrl} alt={name} />
                                ) : (
                                  <AvatarFallback className="text-xs">{initials(name)}</AvatarFallback>
                                )}
                              </Avatar>
                              <span className="text-sm">{name}</span>
                            </div>
                          );
                        })
                      ) : (
                        <span className="text-secondary-text text-sm">—</span>
                      );
                    })()}
                  </div>
                </div>

                {/* Priority */}
                <div className="flex flex-col gap-1">
                  <Label>Priority</Label>
                  <div className="bg-grey-1 ring-grey-3 flex min-h-[41px] flex-wrap items-center gap-1 rounded p-2 ring-2 ring-inset">
                    {workOrder.priority && isWorkOrderPriority(workOrder.priority) ? (
                      <div className="flex items-center gap-1">
                        {workOrder.priority === WorkOrderPriority.Critical ? (
                          <WarningIcon size={20} className="text-default-red" />
                        ) : (
                          <div className="grid size-4 place-items-center">
                            <div className={cn('mx-[3px] h-[15px] w-[15px] rounded-full border-[3px]', getWorkOrderPriorityColorClass(workOrder.priority))} />
                          </div>
                        )}
                        <span className="text-sm">{startCase(workOrder.priority)}</span>
                      </div>
                    ) : (
                      <span className="text-secondary-text text-sm">—</span>
                    )}
                  </div>
                </div>

                {/* Due Date */}
                <div className="flex flex-col gap-1">
                  <Label>Due Date</Label>
                  <div className="bg-grey-1 ring-grey-3 flex min-h-[41px] flex-wrap items-center gap-1 rounded p-2 ring-2 ring-inset">
                    <span className="text-sm">{workOrder.dueDate ? formatDateWithUserTimezone(new Date(workOrder.dueDate), userTimezone) : '—'}</span>
                  </div>
                </div>

                {/* Instructions */}
                {workOrder.instructions && (
                  <div className="flex flex-col gap-1">
                    <Label>Instructions</Label>
                    <div className="bg-grey-1 ring-grey-3 rounded p-2 text-sm ring-2 ring-inset">
                      {workOrder.instructions.split(/\n/).map((line: string, i: number) => (
                        <p key={i}>{line}</p>
                      ))}
                    </div>
                  </div>
                )}

                {/* PO Reference */}
                {workOrder.po && (
                  <div className="flex flex-col gap-1">
                    <Label>PO Reference</Label>
                    <div className="bg-grey-1 ring-grey-3 flex min-h-[41px] flex-wrap items-center gap-1 rounded p-2 ring-2 ring-inset">
                      <span className="text-sm">{workOrder.po}</span>
                    </div>
                  </div>
                )}

                {/* Comments */}
                {workOrder.comments && (
                  <div className="flex flex-col gap-1">
                    <Label>Comments</Label>
                    <div className="bg-grey-1 ring-grey-3 rounded p-2 text-sm ring-2 ring-inset">
                      {workOrder.comments.split(/\n/).map((line: string, i: number) => (
                        <p key={i}>{line}</p>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </ScrollArea>
          </div>
        </SheetContent>
      </Sheet>

      <IssueDrawer
        issueId={selectedIssueId ?? undefined}
        open={!!selectedIssueId}
        onOpenChange={(isOpen) => {
          if (!isOpen) {
            setSelectedIssueId(null);
            setIsOpen(true);
            queryClient.invalidateQueries({ queryKey: awaWorkOrderKeys.all });
          }
        }}
        source={IssueSource.Manual}
        noTrigger
      />

      <CheckDrawer
        checkId={selectedCheckId}
        open={!!selectedCheckId}
        onOpenChange={(isOpen, success) => {
          if (!isOpen) {
            setSelectedCheckId(null);
            setIsOpen(true);
            if (success) {
              if (assetId) {
                queryClient.invalidateQueries({ queryKey: checkKeys.awaByAsset(assetId) });
              }
              queryClient.invalidateQueries({ queryKey: awaWorkOrderKeys.all });
            }
          }
        }}
      />
    </>
  );
}
